<template>
  <div
    v-if="show"
    class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-scroll bg-grey h-full w-full flex items-center justify-center text-base"
  >
    <div class="bg-white rounded-lg shadow-xl w-10/12 max-w-2xl">
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-2xl text-gray-800 font-bold">
          Edit Non-Pharmaceutical Item
        </h4>
        <button
          type="button"
          class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
          @click="$emit('close')"
          aria-label="Close"
        >
          <span class="sr-only">Dismiss</span>
          <svg
            class="w-3 h-3"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <form @submit.prevent="submitForm" class="space-y-4 py-6">
        <div class="grid grid-cols-2 gap-x-20 gap-y-6 px-20">
          <div>
            <label for="code" class="block text-sm font-medium text-gray-700"
              >Item Code<span class="text-red-500">*</span></label
            >
            <input
              type="text"
              id="code"
              v-model="form.code"
              placeholder="Type... (e.g. IT0012)"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            />
            <span class="text-sm text-red-500" v-if="errors.code">
              Item code is required
            </span>
          </div>

          <div>
            <label for="trade_name" class="block text-sm font-medium text-gray-700"
              >Item Name<span class="text-red-500">*</span></label
            >
            <input
              type="text"
              id="trade_name"
              v-model="form.trade_name"
              placeholder="Type... (e.g. Latex Gloves)"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            />
            <span class="text-sm text-red-500" v-if="errors.trade_name">
              Item name is required
            </span>
          </div>

          <div>
            <label for="storageConditions" class="block text-sm font-medium text-gray-700"
              >Storage Conditions<span class="text-red-500">*</span></label
            >
            <select
              id="storageConditions"
              v-model="form.storage_conditions"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            >
              <option
                v-for="storage of storageConditions"
                :key="storage.id"
                :value="storage.label"
              >
                {{ storage.label }}
              </option>
            </select>
            <span class="text-sm text-red-500" v-if="errors.storage_conditions">
              Storage condition is required
            </span>
          </div>

          <div>
            <label for="manufacturer" class="block text-sm font-medium text-gray-700"
              >Manufacturer</label
            >
            <input
              type="text"
              id="manufacturer"
              v-model="form.manufacturer"
              placeholder="Type... (e.g. Plasti Surge Industries Pvt Ltd)"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            />
          </div>
        </div>

        <div class="flex justify-end mt-6 gap-y-6 px-20">
          <button
            type="submit"
            :disabled="showLoader"
            class="px-4 py-2 bg-custom-yellow text-white rounded-md hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-custom-yellow focus:ring-opacity-50"
          >
            <span v-if="!showLoader">Submit</span>
            <span v-else>
              <svg
                class="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                  fill="none"
                />
              </svg>
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";

export default {
  name: "EditNonPharmaceuticalItem",
  props: {
    inventoryItem: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      form: {
        item_type: 2,
        code: "",
        trade_name: "",
        storage_conditions: "",
        manufacturer: "",
      },
      errors: {},
      showLoader: false,
      storageConditions: [
        { id: 1, label: "Normal" },
        { id: 2, label: "Refrigarated" },
      ],
    };
  },
  watch: {
    // When the parent passes a new item to edit, pre-fill the form
    inventoryItem: {
      immediate: true,
      handler(item) {
        if (item && item.id) {
          this.form = {
            item_type: item.item_type,
            code: item.code,
            trade_name: item.trade_name,
            storage_conditions: item.storage_conditions,
            manufacturer: item.manufacturer || "",
          };
        }
      },
    },
  },
  methods: {
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }
      try {
        this.showLoader = true;
        const response = await apiClient.updateInventoryItem(
          this.inventoryItem.id,
          this.form
        );
        if (response.success) {
          this.showLoader = false;
          this.$toast.success("Item updated successfully");
          this.$emit("close");
          this.$router.push("/inventory");
        } else {
          this.showLoader = false;
          this.$toast.error(response.message || "Failed to update item");
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to update item");
        console.log(error);
      }
    },
    validateForm() {
      this.errors = {};
      let count = 0;

      const requiredFields = ["code", "trade_name", "storage_conditions"];

      requiredFields.forEach((field) => {
        if (!this.form[field]) {
          count++;
          this.errors[field] = true;
        }
      });

      return count === 0;
    },
  },
};
</script>